import {
	Component,
	OnInit,
	Input,
	ViewEncapsulation,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	forwardRef,
	AfterViewInit,
	OnDestroy,
	HostListener,
	OnChanges,
	SimpleChanges
} from '@angular/core';
import { IdService, FormControlBase } from '../../shared/index';
import { InputComponent } from '../input/input.component';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { IAutocompleteOption } from './autocomplete.types';

@Component({
	selector: 'rbc-autocomplete',
	templateUrl: './autocomplete.component.html',
	styleUrls: ['./autocomplete.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => AutocompleteComponent)
		}
	]
})
export class AutocompleteComponent extends FormControlBase<IAutocompleteOption>
	implements OnInit, AfterViewInit, OnDestroy, OnChanges {
	@Input() customLookup: any[] = ['key', 'value'];
	@Input() displayKey: boolean = true;
	@Input() helpText: string;
	@Input() id: string;
	@Input() label: string;
	@Input() maxResults: number = 5;
	@Input() options: IAutocompleteOption[];
	@Input() placeholder: string;
	@Input() preselect: string;
	@Input() showAll: boolean = false;
	@Input() size: string = '100';
	@Input() triggerAfter: string | number = 2;

	@Output() selected = new EventEmitter();

	@ViewChild('Card') $Card;
	@ViewChild('CardResults') $CardResults: ElementRef;
	@ViewChild('InputSearch') $InputSearch: ElementRef<InputComponent>;

	open = false; // Whether the options dropdown is being displayed
	query = ''; // The query string attached to the input box
	elements = []; // options with an added display property
	filteredElements = []; // The elements to display based on the query string
	toggleSelectPosition = 0;
	selectedElement = -1; // The index of the selected element in filteredElements
	close = true;

	constructor(private idService: IdService, private eRef: ElementRef) {
		super();
		this.id = idService.generate();
	}

	@HostListener('document:click', ['$event']) clickout(event) {
		if (!this.eRef.nativeElement.contains(event.target) && this.open) {
			this.open = false;
		}
	}

	ngOnInit() {
		this.Autocomplete_init();
	}

	ngAfterViewInit() {
		// Add an event listener for key presses on the menu
		document.getElementById(this.id).addEventListener('keydown', e => {
			if (this.showAll) {
				this.open = true;
			}
			this.$_Autocomplete_toggleSelect(e.key);
		});
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		super.ngOnDestroy();
	}

	ngOnChanges(changes: SimpleChanges): void {
		super.ngOnChanges(changes);

		if (changes && changes.preselect) {
			this.innerValue = changes.preselect.currentValue;
			this.Autocomplete_preselect();
		}
	}

	// Called every time a value is added to the input, creates a new query that is used to filter elements
	queryUpdate(e) {
		this.query = e;
		this.Autocomplete_filters();
	}

	// removes any spans used for to outline the query string in the key
	Autocomplete_cleanKey(key) {
		return key.replace('<span class="queryString">', '').replace('</span>', '');
	}

	// On click that isn't the inputsearch element
	Autocomplete_close(e) {
		this.open = e.target.id !== this.id ? false : true;
	}

	Autocomplete_init() {
		if (typeof this.options !== 'undefined') {
			// Map options to a new array elements that has a display property the same as value if not already defined
			this.elements = this.options.map(el => {
				if (typeof el.display !== 'undefined') {
					return el;
				} else {
					return { display: el.value, ...el };
				}
			});

			// If showAll is true than filtered elements is all elements
			if (this.showAll) {
				this.filteredElements = this.elements;
			}
		}
	}

	// Filters the options to those that contain the query in one of the custom lookup properties
	Autocomplete_filters() {
		this.selectedElement = -1;
		if (
			this.query.indexOf('<') === -1 &&
			this.query.indexOf('>') === -1 &&
			(this.showAll || (this.query.length > this.triggerAfter && this.selectedElement < 0))
		) {
			// For each element
			this.filteredElements = this.elements.filter(el => {
				// Check if the query is in any of the lookup values for each element
				let elementFiltered = false;
				this.customLookup.forEach(filter => {
					if (el[filter].toLowerCase().indexOf(this.query.toLowerCase()) > -1) {
						elementFiltered = true;
					}
				});
				return elementFiltered;
			});
			this.filteredElements = this.filteredElements.map(el => {
				const indexKey = el.key.toLowerCase().indexOf(this.query.toLowerCase());
				const indexValue = el.display.toLowerCase().indexOf(this.query.toLowerCase());
				const newKey = this.Autocomplete_formatDisplay(indexKey, el.key);
				const newValue = this.Autocomplete_formatDisplay(indexValue, el.display);
				return { ...el, key: newKey, value: el.value, display: newValue };
			});
			// Slice the filtered elements to the max number of results in props
			if (this.maxResults > -1 && this.filteredElements.length) {
				this.filteredElements = this.filteredElements.slice(0, this.maxResults);
				this.open = true;
			}
		}
		// Resets the elements when your query is no longer long enough
		else {
			this.filteredElements = [];
			this.toggleSelectPosition = 0;
			this.selectedElement = -1;
			this.open = false;
		}
	}

	// Formats the display property to outline the query in the val prop
	Autocomplete_formatDisplay(index, val) {
		const displayVal =
			index !== -1 && // Index of query was found
			(val.indexOf('>', index) - val.indexOf('<', index) >= 0 && // Check if a closing chevron is before an opening one from the index we are
				(val.indexOf('>', index) !== -1 || val.substring(index, val.indexOf('>', index)).indexOf('<', index) === -1)) // Check if there is a closing chevron but no opening one coming
				? val.substring(0, index) +
				  '<span class="queryString">' +
				  val.substring(index, index + this.query.length) +
				  '</span>' +
				  val.substring(index + this.query.length, val.length)
				: val;
		return displayVal;
	}

	// Set the preselected value
	Autocomplete_preselect() {
		if (typeof this.options !== 'undefined') {
			if (this.preselect && this.preselect !== '') {
				this.options.forEach((el, index) => {
					if (el.key === this.preselect) {
						// this.Autocomplete_updateValue(index, false);
						this.query = this.setQuery(el);
					}
				});
			} else if (this.preselect === '') {
				this.query = '';
			}
		}
	}

	// Toggle the selected value from the dropdown depending on the key that is pressed
	$_Autocomplete_toggleSelect(key) {
		const nbResults = this.filteredElements.length;
		if (nbResults > 0) {
			// Down arrow pressed
			if (key.toLowerCase().indexOf('down') > -1 && this.toggleSelectPosition < nbResults - 1) {
				this.toggleSelectPosition++;
				this.$Card.scrollBody(this.$CardResults.nativeElement.querySelector('.active').offsetTop);
			}
			// Up arrow pressed
			else if (key.toLowerCase().indexOf('up') > -1 && this.toggleSelectPosition > 0) {
				this.toggleSelectPosition--;
				const scrollVal =
					this.$CardResults.nativeElement.querySelector('.active').offsetTop -
					this.$CardResults.nativeElement.querySelector('.active').offsetHeight * 2;
				this.$Card.scrollBody(scrollVal);
			}
			// Enter pressed
			else if (key.toLowerCase() === 'enter') {
				this.open = false;
				this.Autocomplete_updateValue(null);
				this.toggleSelectPosition = 0;
			}
			// Escape pressed
			else if (key.toLowerCase() === 'escape') {
				this.open = false;
				this.Autocomplete_clear();
			}
		}
	}

	setQuery(chosenElement: IAutocompleteOption): string {
		let query = '';
		query = this.displayKey
			? chosenElement.key.replace('<span class="queryString">', '').replace('</span>', '') + ' - '
			: '';
		query += chosenElement.value.replace('<span class="queryString">', '').replace('</span>', '');
		chosenElement.key = chosenElement.key.replace('<span class="queryString">', '').replace('</span>', '');
		chosenElement.value = chosenElement.value.replace('<span class="queryString">', '').replace('</span>', '');

		return query;
	}

	// Updates the value in the input on click of the dropdown or on preselect
	Autocomplete_updateValue(position) {
		this.selectedElement = position ? position : this.toggleSelectPosition;
		// Get the chosen element object based on it's index in filter elements
		const chosenElement = this.options[this.selectedElement];
		// if display key is true then the query is the chosen elements key with a dash before the value
		this.query = this.setQuery(chosenElement);
		this.selected.emit(chosenElement);
		this.setModel(chosenElement);
	}

	Autocomplete_clear() {
		// Reset component
		this.query = '';
		this.selectedElement = -1;
		this.filteredElements = this.showAll ? this.elements : [];
		this.setModel(null);
	}

	writeValue(val: IAutocompleteOption) {
		super.writeValue(val);
		if (val) {
			this.query = this.setQuery(val);
		}
	}

	activeDescendant() {
		if (this.open) {
			return `${this.id}Option${this.filteredElements[this.toggleSelectPosition].key}`;
		} else {
			return this.selectedElement !== -1 ? `${this.id}Option${this.filteredElements[this.selectedElement].key}` : '';
		}
	}
}
